import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue";
const _hoisted_1 = {
    class: "px-6 pb-8",
    "data-testid": "subscription-detail-view"
};
const _hoisted_2 = { class: "mb-4" };
const _hoisted_3 = {
    key: 0,
    class: "animate-pulse space-y-3"
};
const _hoisted_4 = {
    key: 1,
    class: "flex items-start justify-between gap-4"
};
const _hoisted_5 = { class: "text-[1.75rem] font-bold leading-tight" };
const _hoisted_6 = {
    key: 0,
    class: "text-sm text-(--colors-text-secondary) mt-0.5 font-mono"
};
const _hoisted_7 = {
    key: 1,
    class: "mt-1 flex items-center gap-2"
};
const _hoisted_8 = {
    key: 0,
    class: "text-sm text-(--colors-text-secondary)"
};
const _hoisted_9 = { class: "flex items-center justify-between mb-6" };
const _hoisted_10 = { class: "pb-1" };
const _hoisted_11 = ["id", "aria-labelledby"];
import { Badge, Button, ButtonGroup, buttonGroupTabId, TextLink, FormSaveButton, UnsavedChangesModal, } from "@eudonet/opal";
import { computed, reactive, ref, watch, onMounted, onUnmounted, useId } from "vue";
import { RouterLink, useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useDirtyState } from "@/shared/composables/forms/useDirtyState";
import { handleNavigationFailure } from "@/shared/utils/navigation";
import { isClosedSubscription, } from "../../domain/entities/Subscription";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { useSubscriptionStore } from "../../stores/useSubscriptionStore";
import { toUpdateSubscriptionPayload } from "../../infrastructure/mappers/subscriptionMapper";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import { useAppToast } from "@/shared/composables/useAppToast";
import { useSubscriptionOptions } from "../composables/useSubscriptionOptions";
import { subscriptionStatusIcon, subscriptionStatusTheme } from "../composables/subscriptionStatusBadge";
import SubscriptionTabResume from "../components/subscription-detail/SubscriptionTabResume.vue";
import SubscriptionTabDetails from "../components/subscription-detail/SubscriptionTabDetails.vue";
import SubscriptionTabPayments from "../components/subscription-detail/SubscriptionTabPayments.vue";
const DEFAULT_TAB = "resume";
export default /*@__PURE__*/ _defineComponent({
    __name: 'SubscriptionDetailView',
    setup(__props) {
        const { t } = useI18n();
        const { formatAmount } = useFinanceFormatters();
        const router = useRouter();
        const route = useRoute();
        const subscriptionStore = useSubscriptionStore();
        const { toastSuccess, toastError } = useAppToast();
        const { toOption } = useSubscriptionOptions();
        const subscriptionId = computed(() => route.params.subscriptionId);
        // Business label: "Donor name — Amount / Frequency" (OP-5859)
        const subscriptionLabel = computed(() => (loaded.value ? toOption(loaded.value).label : ""));
        // ─── Load from BFF ────────────────────────────────────────────────────────────
        onMounted(async () => {
            if (!subscriptionId.value)
                return;
            // Load subscription detail and payment history in parallel
            await Promise.all([
                subscriptionStore.loadSubscription(subscriptionId.value),
                subscriptionStore.loadSubscriptionDonations(subscriptionId.value),
            ]);
            if (subscriptionStore.currentSubscription) {
                Object.assign(state, subscriptionStore.currentSubscription);
                markAsSaved();
            }
        });
        onUnmounted(() => subscriptionStore.clearCurrentSubscription());
        // ─── Reactive form state (populated by BFF on load) ─────────────────────────
        // Partial, not Subscription: it starts empty and is filled by `Object.assign` once the BFF
        // read lands, so every field is genuinely absent until then.
        const state = reactive({});
        /**
         * The loaded subscription, or `null` before the BFF read lands.
         *
         * Everything that renders or submits subscription fields is gated on this, which is what makes
         * the single assertion safe: `state` is only ever partially filled while `id` is absent, and
         * `Object.assign` copies a complete {@link Subscription} in. One narrowing point here replaces
         * the `state as Subscription` casts that were previously scattered across the template.
         */
        const loaded = computed(() => (state.id ? state : null));
        // The SAVED status, not the form's: picking "Annulé" in the selector must not lock the form
        // before that change is saved.
        const isClosed = computed(() => isClosedSubscription(subscriptionStore.currentSubscription?.status));
        /**
         * Applies one edit from the details tab, normalizing the tab's `null` clear-signal to the
         * `undefined` the domain uses for an absent value.
         *
         * The write needs one assertion: with `key` typed as the whole `keyof Subscription` union, TS
         * narrows the assignable type to the intersection of every field type (effectively `never`).
         * The emit signature already guarantees `key` names a real field, so widening the target to
         * the field-value union is sound.
         */
        function applyFieldUpdate(key, value) {
            state[key] =
                value ?? undefined;
        }
        // ─── Secondary data (placeholder — replace with BFF endpoints) ──────────────
        const donorStats = {
            paymentCount: 0,
        };
        // ─── Save ─────────────────────────────────────────────────────────────────────
        const isSaving = ref(false);
        // ─── Dirty state ─────────────────────────────────────────────────────────────
        const showUnsavedChangesModal = ref(false);
        const pendingTabKey = ref(null);
        const pendingRouteCallbacks = {
            proceed: null,
            cancel: null,
        };
        const { isDirty, markAsSaved, resetToOriginal } = useDirtyState({
            data: state,
            onNavigationBlocked: (proceed, cancel) => {
                showUnsavedChangesModal.value = true;
                pendingRouteCallbacks.proceed = proceed;
                pendingRouteCallbacks.cancel = cancel;
            },
            enableBeforeUnload: true,
        });
        watch(() => subscriptionStore.currentSubscription, (sub) => {
            if (sub) {
                Object.assign(state, sub);
                markAsSaved();
            }
        });
        // ─── Tabs ─────────────────────────────────────────────────────────────────────
        const tabs = computed(() => [
            { key: "resume", label: t("finance.subscriptions.tabs.resume") },
            { key: "details", label: t("finance.subscriptions.tabs.details") },
            { key: "payments", label: t("finance.subscriptions.tabs.donationHistory") },
        ]);
        const VALID_TABS = ["resume", "details", "payments"];
        const panelId = useId();
        const activeTab = computed(() => {
            const q = route.query.tab;
            return typeof q === "string" && VALID_TABS.includes(q)
                ? q
                : DEFAULT_TAB;
        });
        function navigateToTab(tabKey) {
            const { tab: _tab, ...rest } = route.query;
            const nextQuery = tabKey === DEFAULT_TAB ? rest : { ...rest, tab: tabKey };
            router.push({ query: nextQuery }).catch(handleNavigationFailure("SubscriptionDetailView:tab"));
        }
        // ─── Handlers ─────────────────────────────────────────────────────────────────
        const handleTabChange = (tabKey) => {
            if (tabKey === activeTab.value)
                return;
            if (isDirty.value) {
                pendingTabKey.value = tabKey;
                showUnsavedChangesModal.value = true;
            }
            else {
                navigateToTab(tabKey);
            }
        };
        // Maps the backend validator/domain error codes to localized, actionable toast messages so a
        // French/Dutch user sees more than "Request failed with status code 400" (R70).
        const SUBSCRIPTION_SAVE_ERROR_KEYS = {
            "Subscription.Amount.Positive": "finance.subscriptions.errors.amountPositive",
            "Subscription.InitialAmount.Positive": "finance.subscriptions.errors.initialAmountPositive",
            "Subscription.InstallmentCount.Invalid": "finance.subscriptions.errors.installmentCountInvalid",
            "Subscription.StartDate.Required": "finance.subscriptions.errors.startDateRequired",
            "Subscription.StartDate.InvalidRange": "finance.subscriptions.errors.startDateRange",
            "Subscription.EndDate.InvalidRange": "finance.subscriptions.errors.endDateRange",
            "Subscription.Status.Invalid": "finance.subscriptions.errors.statusInvalid",
            SubscriptionInvalidTransition: "finance.subscriptions.errors.statusTransition",
            "Subscription.Status.Closed": "finance.subscriptions.errors.closed",
        };
        const handleSave = async () => {
            // Save is only reachable once the BFF read has landed; the guard keeps that explicit.
            if (!loaded.value)
                return;
            isSaving.value = true;
            try {
                await subscriptionStore.updateSubscription(subscriptionId.value, toUpdateSubscriptionPayload(loaded.value));
                markAsSaved();
                toastSuccess("finance.subscriptions.saveSuccess");
            }
            catch (e) {
                const code = getApiErrorCode(e);
                toastError((code && SUBSCRIPTION_SAVE_ERROR_KEYS[code]) || "finance.subscriptions.saveError");
            }
            finally {
                isSaving.value = false;
            }
        };
        const handleStay = () => {
            showUnsavedChangesModal.value = false;
            pendingTabKey.value = null;
            if (pendingRouteCallbacks.cancel) {
                pendingRouteCallbacks.cancel();
                pendingRouteCallbacks.proceed = null;
                pendingRouteCallbacks.cancel = null;
            }
        };
        // Resume whatever the guard interrupted — a queued tab switch or a real route
        // navigation. Shared by the discard path (after reverting) and the
        // save-and-continue path (after a successful persist).
        function proceedPendingNavigation() {
            if (pendingTabKey.value) {
                const target = pendingTabKey.value;
                pendingTabKey.value = null;
                markAsSaved();
                navigateToTab(target);
            }
            if (pendingRouteCallbacks.proceed) {
                pendingRouteCallbacks.proceed();
                pendingRouteCallbacks.proceed = null;
                pendingRouteCallbacks.cancel = null;
            }
        }
        const handleLeave = () => {
            showUnsavedChangesModal.value = false;
            resetToOriginal();
            proceedPendingNavigation();
        };
        // Modal primary "Enregistrer les modifications" — persist first (handleSave owns
        // the toast), then resume the interrupted navigation only if the save cleared
        // the dirty flag. A failed save keeps the form dirty and the modal open.
        async function handleSaveAndContinue() {
            await handleSave();
            if (isDirty.value)
                return;
            showUnsavedChangesModal.value = false;
            proceedPendingNavigation();
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createVNode(_unref(TextLink), {
                            as: _unref(RouterLink),
                            "icon-left": "ri-arrow-left-line",
                            to: { name: 'SubscriptionList' },
                            class: "mb-3"
                        }, {
                            default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t("finance.subscriptions.detail.backButton")), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["as"]),
                        (_unref(subscriptionStore).isLoading && !state.id)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[2] || (_cache[2] = [
                                    _createElementVNode("div", { class: "h-8 w-48 bg-gray-200 rounded" }, null, -1 /* CACHED */),
                                    _createElementVNode("div", { class: "h-5 w-32 bg-gray-100 rounded" }, null, -1 /* CACHED */)
                                ]))]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                _createElementVNode("div", null, [
                                    _createElementVNode("h1", _hoisted_5, _toDisplayString(subscriptionLabel.value || state.id || "…"), 1 /* TEXT */),
                                    (subscriptionLabel.value && state.id)
                                        ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(state.id), 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true),
                                    (state.status)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                            _createVNode(_unref(Badge), {
                                                theme: _unref(subscriptionStatusTheme)[state.status],
                                                label: _ctx.$t(`finance.subscriptions.status.${state.status}`),
                                                "show-close-icon": false,
                                                size: "sm",
                                                "left-icon": _unref(subscriptionStatusIcon)[state.status]
                                            }, null, 8 /* PROPS */, ["theme", "label", "left-icon"]),
                                            (state.frequency)
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(_ctx.$t(`finance.subscriptions.frequency.${state.frequency}`)) + " · " + _toDisplayString(_unref(formatAmount)(state.amount ?? 0, state.currency)), 1 /* TEXT */))
                                                : _createCommentVNode("v-if", true)
                                        ]))
                                        : _createCommentVNode("v-if", true)
                                ])
                            ]))
                    ]),
                    _createElementVNode("div", _hoisted_9, [
                        _createVNode(_unref(ButtonGroup), {
                            "model-value": activeTab.value,
                            "panel-id": _unref(panelId),
                            "overflow-label": _ctx.$t('common.tabs.next'),
                            "overflow-previous-label": _ctx.$t('common.tabs.previous'),
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (handleTabChange($event)))
                        }, {
                            default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (tab) => {
                                    return (_openBlock(), _createBlock(_unref(Button), {
                                        key: tab.key,
                                        variant: "tertiary",
                                        size: "xs",
                                        label: tab.label,
                                        value: tab.key
                                    }, null, 8 /* PROPS */, ["label", "value"]));
                                }), 128 /* KEYED_FRAGMENT */))
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["model-value", "panel-id", "overflow-label", "overflow-previous-label"]),
                        _createElementVNode("div", _hoisted_10, [
                            (!isClosed.value)
                                ? (_openBlock(), _createBlock(_unref(FormSaveButton), {
                                    key: 0,
                                    enabled: _unref(isDirty),
                                    saving: isSaving.value,
                                    "save-label": _ctx.$t('finance.subscriptions.detail.saveChanges'),
                                    "saving-label": _ctx.$t('finance.subscriptions.detail.saving'),
                                    "disabled-hint": _ctx.$t('form.saveDisabledHint'),
                                    onSave: handleSave
                                }, null, 8 /* PROPS */, ["enabled", "saving", "save-label", "saving-label", "disabled-hint"]))
                                : _createCommentVNode("v-if", true)
                        ])
                    ]),
                    (loaded.value)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            id: _unref(panelId),
                            role: "tabpanel",
                            "aria-labelledby": _unref(buttonGroupTabId)(_unref(panelId), activeTab.value)
                        }, [
                            (activeTab.value === 'resume')
                                ? (_openBlock(), _createBlock(SubscriptionTabResume, {
                                    key: 0,
                                    state: loaded.value,
                                    "donor-stats": donorStats
                                }, null, 8 /* PROPS */, ["state"]))
                                : (activeTab.value === 'details')
                                    ? (_openBlock(), _createBlock(SubscriptionTabDetails, {
                                        key: 1,
                                        state: loaded.value,
                                        readonly: isClosed.value,
                                        "onUpdate:field": applyFieldUpdate
                                    }, null, 8 /* PROPS */, ["state", "readonly"]))
                                    : (activeTab.value === 'payments')
                                        ? (_openBlock(), _createBlock(SubscriptionTabPayments, {
                                            key: 2,
                                            payments: _unref(subscriptionStore).subscriptionDonations
                                        }, null, 8 /* PROPS */, ["payments"]))
                                        : _createCommentVNode("v-if", true)
                        ], 8 /* PROPS */, _hoisted_11))
                        : _createCommentVNode("v-if", true)
                ]),
                _createVNode(_unref(UnsavedChangesModal), {
                    modelValue: showUnsavedChangesModal.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((showUnsavedChangesModal).value = $event)),
                    title: _ctx.$t('leaveWithoutSaving.title'),
                    message: _ctx.$t('leaveWithoutSaving.message'),
                    "stay-label": _ctx.$t('leaveWithoutSaving.stay'),
                    "leave-label": _ctx.$t('leaveWithoutSaving.leave'),
                    "save-label": _ctx.$t('leaveWithoutSaving.save'),
                    "save-disabled": isSaving.value,
                    onDiscard: handleLeave,
                    onSave: handleSaveAndContinue,
                    onStay: handleStay
                }, null, 8 /* PROPS */, ["modelValue", "title", "message", "stay-label", "leave-label", "save-label", "save-disabled"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
